import { useEffect, useRef, useState } from 'preact/hooks'
import { AlertTriangle, ChevronRight } from 'lucide-preact'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import type { ValidationError } from '@/utils/errorHandling'
import { toReadinessItems } from '@/constants/signOffReadiness'

interface Props {
    /** Server-computed outstanding list from the Web view DTO. */
    outstanding: ValidationError[] | undefined
    routeToTab: (tab: WebTabKey) => void
}

/**
 * Count of what is still outstanding before sign-off, sitting immediately before the sign-off
 * control so the gate and the reason it will fail are adjacent (ORB-460).
 *
 * The chip is the trigger, not the list — a five-item checklist would blow out a header already
 * carrying a title, a date, three chips, audiences and the progress card. It renders nothing when
 * the count is zero: at that point the Sign Off button is enabled and needs no explanation.
 *
 * Amber treatment matches SpeakerCard's missing-fields chip, the app's existing completeness
 * affordance.
 */
export function SignOffReadinessChip({ outstanding, routeToTab }: Props) {
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLSpanElement>(null)
    const items = toReadinessItems(outstanding)

    useEffect(() => {
        if (!open) return
        const onClick = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
        }
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
        document.addEventListener('click', onClick)
        window.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('click', onClick)
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    if (items.length === 0) return null

    return (
        <span class="relative inline-flex" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                aria-label={`${items.length} ${items.length === 1 ? 'item' : 'items'} outstanding before sign-off`}
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
                <AlertTriangle class="w-3.5 h-3.5 flex-shrink-0" />
                {items.length} before sign-off
            </button>

            {open && (
                <div class="absolute left-0 top-full mt-2 z-30 w-72 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
                    <div class="px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Needed before sign-off
                    </div>
                    <ul>
                        {items.map((item, index) => (
                            <li
                                key={`${item.message}-${index}`}
                                class="border-b border-slate-200 dark:border-slate-700 last:border-b-0"
                            >
                                {item.tab ? (
                                    <button
                                        type="button"
                                        onClick={() => { setOpen(false); routeToTab(item.tab as WebTabKey) }}
                                        class="w-full flex items-center justify-between gap-3 px-3.5 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <span>{item.message}</span>
                                        <span class="inline-flex items-center gap-0.5 text-[11px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                                            {item.tabLabel}
                                            <ChevronRight class="w-3 h-3" />
                                        </span>
                                    </button>
                                ) : (
                                    // No tab owns this field name, so there is nothing to link to. Showing
                                    // the server's message alone beats guessing a destination.
                                    <div class="px-3.5 py-2 text-xs text-slate-700 dark:text-slate-200">
                                        {item.message}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </span>
    )
}
